import { Button } from '@/components/core/Button/Button';
import { Callout } from '@/components/core/Callout';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { useTranslation } from '@/hooks/useTranslation';

interface DamDeleteAssetsDialogProps {
    count: number;
    /**
     * Linked articles of a single asset (detail page); when given, the dialog asks for that
     * one asset and lists them. `undefined` keeps the bulk wording of the library.
     */
    linkedArticles?: string[];
    /** Number of assets processed while deleting; `undefined` before confirmation. */
    progress?: number;
    onConfirm: () => void;
    onClose: () => void;
}

/**
 * Confirmation before deleting assets (not drawn in the wireframe). In the library it shows
 * the progress of the bulk delete (D-037); on the detail page it names the linked articles,
 * whose links are removed while the articles remain (Make "Delete Asset", D-043).
 */
function DamDeleteAssetsDialog({ count, linkedArticles, progress, onConfirm, onClose }: DamDeleteAssetsDialogProps) {
    const { t } = useTranslation();
    const isRunning = progress !== undefined;
    const isSingle = linkedArticles !== undefined;

    const title = isSingle ? t('DAM.DETAIL.DELETE_TITLE') : t('DAM.BULK.DELETE_TITLE', { count: count.toLocaleString() });
    const text = !isSingle
        ? t('DAM.BULK.DELETE_TEXT')
        : linkedArticles.length > 0 ? t('DAM.DETAIL.DELETE_TEXT_LINKED') : t('DAM.DETAIL.DELETE_TEXT');

    return (
        <Dialog open onOpenChange={(open) => !open && !isRunning && onClose()}>
            <DialogContent variant="small">
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>{title}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-flex tw-flex-col tw-gap-4">
                    <p className="tw-m-0 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{text}</p>
                    {isSingle && linkedArticles.length > 0 && (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <span className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{t('DAM.DETAIL.DELETE_LINKED_INTRO')}</span>
                            <ul className="tw-m-0 tw-flex tw-max-h-40 tw-list-none tw-flex-col tw-gap-1 tw-overflow-y-auto tw-p-0">
                                {linkedArticles.map((article) => (
                                    <li key={article} className="tw-rounded-lg tw-bg-fill-secondary tw-px-3 tw-py-2 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                        {article}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {isRunning && !isSingle && (
                        <Callout variant="info" canBeClosed={false}>
                            <span className="tw-text-t-sm lg:tw-text-t-base" role="status">
                                {t('DAM.BULK.DELETE_PROGRESS', { done: progress.toLocaleString(), count: count.toLocaleString() })}
                            </span>
                        </Callout>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" disabled={isRunning} onClick={onClose}>{t('CANCEL')}</Button>
                    <Button variant="destructive" loading={isRunning} disabled={isRunning} onClick={onConfirm}>
                        {isSingle ? t('DAM.DETAIL.DELETE_CONFIRM') : t('DAM.BULK.DELETE')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { DamDeleteAssetsDialog };
export type { DamDeleteAssetsDialogProps };
